import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import ToastContainer, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, loadModuleData, saveModuleData, addActivityAsync, generateSWOTFromSQAAF, getSQAAFStatus } from "./utils";

const TAG = {
  green: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]",
  blue: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]",
  orange: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]",
  yellow: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]",
  red: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]",
};

function Tag({ cls = "blue", children }) {
  return (
    <span className={`inline-flex items-center gap-[6px] px-[10px] py-[6px] rounded-full text-[11px] font-semibold border whitespace-nowrap ${TAG[cls] || TAG.blue}`}>
      {children}
    </span>
  );
}

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white border border-[#e6ecf3] rounded-[26px] shadow-[0_16px_36px_rgba(19,31,47,.07)] overflow-hidden">
      <div className="px-5 py-[18px] border-b border-[#e6ecf3] flex items-center justify-between gap-3">
        <div>
          <div className="text-[16px] font-semibold text-[#233445] mb-[2px]">{title}</div>
          {note && <div className="text-[12px] text-[#617587]">{note}</div>}
        </div>
        {headerRight && <div className="flex flex-wrap gap-[10px]">{headerRight}</div>}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

const EMPTY_POINT = { quadrant: "", priority: "", title: "", description: "", source: "", actionHint: "" };

function calculateCompletion(points) {
  const strengths = points.filter(p => p.quadrant === "Strength").length;
  const weaknesses = points.filter(p => p.quadrant === "Weakness").length;
  const opportunities = points.filter(p => p.quadrant === "Opportunity").length;
  const threats = points.filter(p => p.quadrant === "Threat").length;
  const highPriority = points.filter(p => p.priority === "High").length;

  let filled = 0;
  if (strengths > 0) filled++;
  if (weaknesses > 0) filled++;
  if (opportunities > 0) filled++;
  if (threats > 0) filled++;
  if (points.length >= 4) filled++;
  if (points.length >= 6) filled++;
  if (highPriority > 0) filled++;
  if (highPriority >= 2) filled++;

  return Math.round((filled / 8) * 100);
}

function quadrantCls(q) {
  if (q === "Strength") return "green";
  if (q === "Weakness") return "red";
  if (q === "Opportunity") return "blue";
  return "yellow";
}

function priorityCls(p) {
  if (p === "High") return "red";
  if (p === "Medium") return "orange";
  return "blue";
}

export default function SWOT() {
  const settings = getSchoolSettings();
  const [points, setPoints] = useState(() => {
    const stored = readStorage(STORAGE_KEYS.swot, null);
    return Array.isArray(stored?.points) ? stored.points : [];
  });
  const [form, setForm] = useState(EMPTY_POINT);
  const [editingIndex, setEditingIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("entry");
  const [searchQuery, setSearchQuery] = useState("");
  const [quadrantFilter, setQuadrantFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [quickQuadrant, setQuickQuadrant] = useState("Strength");
  const [sqaafAuditId, setSqaafAuditId] = useState(null);
  const [showSQAAFBanner, setShowSQAAFBanner] = useState(false);
  const [generatingSQAAF, setGeneratingSQAAF] = useState(false);
  const [previewPoints, setPreviewPoints] = useState([]);
  const [previewSelected, setPreviewSelected] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewAuditId, setPreviewAuditId] = useState(null);

  useEffect(() => {
    loadModuleData("swot", null).then(d => {
      if (d?.points) setPoints(d.points);
      const storedAuditId = d?.sqaaf_audit_id || null;
      setSqaafAuditId(storedAuditId);
      getSQAAFStatus().then(status => {
        if (status?.has_audit && storedAuditId && status.audit_id && status.audit_id !== storedAuditId) {
          setShowSQAAFBanner(true);
        }
      });
    });
  }, []);

  function persist(list, label = "SWOT saved", auditId = null) {
    const data = auditId ? { points: list, sqaaf_audit_id: auditId } : { points: list };
    saveModuleData("swot", data, calculateCompletion(list));
    addActivityAsync("SWOT", label, "Saved", "green");
  }

  async function handleGenerateFromSQAAF() {
    setGeneratingSQAAF(true);
    try {
      const res = await generateSWOTFromSQAAF();
      if (!res || !res.points || res.points.length === 0) {
        showToast("No SQAAF data found. Complete a SQAAF audit first.", "warn");
        return;
      }
      setPreviewPoints(res.points);
      setPreviewSelected(res.points.map((_, i) => i));
      setPreviewAuditId(res.sqaaf_audit_id);
      setShowPreviewModal(true);
    } catch (err) {
      showToast("Failed to generate SWOT from SQAAF. Try again.", "error");
    } finally {
      setGeneratingSQAAF(false);
    }
  }

  function handlePreviewConfirm() {
    const selected = previewPoints.filter((_, i) => previewSelected.includes(i));
    const merged = [...points, ...selected];
    setSqaafAuditId(previewAuditId);
    setShowSQAAFBanner(false);
    persist(merged, "SWOT generated from SQAAF", previewAuditId);
    setPoints(merged);
    setShowPreviewModal(false);
    setPreviewPoints([]);
    setPreviewSelected([]);
    showToast(`${selected.length} SWOT point(s) imported from SQAAF.`, "success");
  }

  function togglePreviewSelect(i) {
    setPreviewSelected(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  }

  function handleAdd() {
    if (!form.quadrant || !form.priority || !form.title.trim() || !form.description.trim()) {
      showToast("Quadrant, Priority, Point Title, and Description are required.", "error");
      return;
    }
    let updated;
    if (editingIndex !== null) {
      updated = points.map((p, i) => i === editingIndex ? form : p);
      persist(updated, "SWOT point updated");
      showToast("SWOT point updated.", "success");
    } else {
      updated = [...points, form];
      persist(updated, "SWOT point added");
      showToast("SWOT point added.", "success");
    }
    setPoints(updated);
    setForm(EMPTY_POINT);
    setEditingIndex(null);
  }

  function handleEdit(i) {
    setForm(points[i]);
    setEditingIndex(i);
    setActiveTab("entry");
  }

  function handleDelete(i) {
    const updated = points.filter((_, idx) => idx !== i);
    persist(updated, "SWOT point deleted");
    setPoints(updated);
    showToast("SWOT point deleted.", "warn");
  }

  function handleFillSample() {
    const sample = [
      { quadrant: "Strength", priority: "High", title: "Strong discipline reputation among parents", description: "The school is widely known for its structured environment and consistent discipline standards.", source: "Internal Observation", actionHint: "Maintain and strengthen through consistent standards reinforcement" },
      { quadrant: "Strength", priority: "Medium", title: "Dedicated and stable teaching staff", description: "The school has a low teacher turnover rate and a core of experienced educators.", source: "Internal Observation", actionHint: "Leverage for mentoring newer staff" },
      { quadrant: "Weakness", priority: "High", title: "Inconsistent instructional quality across grades", description: "Teaching delivery varies significantly between classes and sections, impacting learning outcomes.", source: "Diagnosis", actionHint: "Standardize through classroom observation and peer review cycles" },
      { quadrant: "Weakness", priority: "Medium", title: "Limited structured parent engagement", description: "Parent involvement is mostly reactive rather than part of a structured engagement program.", source: "Market Dynamics", actionHint: "Design and launch a regular parent communication calendar" },
      { quadrant: "Opportunity", priority: "High", title: "Growing parent demand for academic transparency", description: "Parents in the catchment area increasingly want more visibility into classroom learning and progress.", source: "Market Dynamics", actionHint: "Develop transparent progress reporting and parent update systems" },
      { quadrant: "Threat", priority: "High", title: "Increasing competition from new private schools", description: "Several new schools have opened in the area with stronger marketing and modern facilities.", source: "Market Dynamics", actionHint: "Strengthen differentiators and clearly communicate school advantages" },
    ];
    persist(sample, "Sample SWOT loaded");
    setPoints(sample);
    showToast("Sample SWOT content loaded.", "success");
  }

  function handleClear() {
    if (!window.confirm("This will clear all saved SWOT data. Continue?")) return;
    saveModuleData("swot", { points: [] }, 0);
    addActivityAsync("SWOT", "Module cleared", "Cleared", "red");
    setPoints([]);
    setForm(EMPTY_POINT);
    setEditingIndex(null);
    showToast("SWOT module cleared.", "warn");
  }

  function handleQuickQuadrant(e) {
    setQuickQuadrant(e.target.value);
    setForm(f => ({ ...f, quadrant: e.target.value }));
    showToast(`Quadrant preselected as ${e.target.value}.`, "success");
  }

  const completion = calculateCompletion(points);
  const highPriority = points.filter(p => p.priority === "High").length;
  const readiness = completion >= 85 ? "High" : completion >= 55 ? "Medium" : "Low";
  const interpretation = completion >= 85
    ? "The school has a fairly strong SWOT and can now move into goal-setting with good strategic clarity."
    : completion >= 55
    ? "The school has a partial SWOT, but some quadrants still need stronger coverage."
    : "The school still needs a fuller SWOT before strong goal-setting can begin.";

  const byQuadrant = (q) => points.filter(p => p.quadrant === q);
  const SWOT_QUADRANT_OPTIONS = ["Strength","Weakness","Opportunity","Threat"];
  const SWOT_PRIORITY_OPTIONS = ["High","Medium","Low"];
  const SWOT_SOURCE_OPTIONS = ["Diagnosis","Market Dynamics","Internal Observation","Leadership Input"];

  const filtered = points.filter(p =>
    (!searchQuery.trim() || p.title.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!quadrantFilter || p.quadrant === quadrantFilter) &&
    (!priorityFilter || p.priority === priorityFilter) &&
    (!sourceFilter || p.source === sourceFilter)
  );

  const QUADRANTS = [
    { q: "Strength", bg: "bg-[#eaf8f1]", tagCls: "green" },
    { q: "Weakness", bg: "bg-[#fff0f0]", tagCls: "red" },
    { q: "Opportunity", bg: "bg-[#eef6fc]", tagCls: "blue" },
    { q: "Threat", bg: "bg-[#fff7e7]", tagCls: "yellow" },
  ];

  const tabBtn = (tab, label) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-5 py-[10px] rounded-[12px] text-[13px] font-semibold transition-colors ${
        activeTab === tab ? "bg-[#f47c20] text-white" : "bg-white text-[#617587] border border-[#e6ecf3] hover:bg-[#eef6fc] hover:text-[#2d81c1]"
      }`}
    >
      {label}
    </button>
  );

  return (

    <>
      <div className="flex-1 min-w-0 flex flex-col border-l-2 border-slate-200 ml-1">

        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
            <section className="mb-8">
              <div className="text-center px-4 py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">SWOT Analysis</h1>
                <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                  Convert school diagnosis and market understanding into strategic strengths, weaknesses, opportunities, and threats.
                </p>
              </div>
            </section>

            <div className="mb-[18px] px-5 py-4 rounded-[16px] bg-[#eef6fc] border border-[#cfe3f3]">
              <div className="text-[13px] text-[#617587] max-w-[980px]">
                This module helps the school identify what to build on, what to fix, what to leverage externally, and what to protect against before moving into goals.
              </div>
            </div>

            {/* SQAAF Banner */}
            {showSQAAFBanner && (
              <div className="mb-[18px] flex items-center justify-between gap-4 px-5 py-4 rounded-[16px] bg-[#fff7e7] border border-[#f4deab]">
                <div className="flex items-center gap-3">
                  <span className="text-[18px]">⚠️</span>
                  <div>
                    <div className="text-[13px] font-semibold text-[#d58d1f]">Your SQAAF has a newer audit cycle</div>
                    <div className="text-[12px] text-[#9a7233]">The SWOT below was generated from an older SQAAF audit. Regenerate to reflect the latest data.</div>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => setShowSQAAFBanner(false)} className="px-3 py-[7px] rounded-[10px] border border-[#f4deab] bg-white text-[#d58d1f] text-[12px] font-semibold hover:bg-[#fff4eb] transition-colors">Dismiss</button>
                  <button onClick={handleGenerateFromSQAAF} disabled={generatingSQAAF} className="px-3 py-[7px] rounded-[10px] bg-[#d58d1f] text-white text-[12px] font-semibold hover:bg-[#b8781a] transition-colors disabled:opacity-50">{generatingSQAAF ? "Analyzing…" : "Regenerate SWOT"}</button>
                </div>
              </div>
            )}

            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Module Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${completion}%`, meta: "Based on SWOT coverage across all four quadrants", bar: true },
                { label: "Total SWOT Points", icon: "🧠", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: points.length, meta: "Combined items across all quadrants" },
                { label: "High Priority Items", icon: "🔥", iconCls: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]", value: highPriority, meta: "Points marked as high strategic priority" },
                { label: "Strategic Readiness", icon: "🧭", iconCls: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]", value: readiness, meta: "How ready the school is to move into goal setting" },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-[10px] mb-3">
                    <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                    <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>{s.icon}</div>
                  </div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                  {s.bar && <div className="w-full h-[6px] rounded-full bg-[#edf2f8] overflow-hidden mt-2"><div className="h-full rounded-full bg-gradient-to-r from-[#f47c20] to-[#2d81c1]" style={{ width: `${completion}%` }} /></div>}
                </div>
              ))}
            </section>

            <div className="flex gap-3 mb-[18px]">
              {tabBtn("entry", "Add SWOT Point")}
              {tabBtn("matrix", "SWOT Matrix")}
              {tabBtn("preview", "Live Preview")}
            </div>

            {activeTab === "entry" && (
              <div className="grid gap-[18px]" style={{ gridTemplateColumns: "1.2fr .8fr" }}>
                <Window title="SWOT Point Entry" note="Add one point at a time with the right quadrant and priority." headerRight={
                  <>
                    <button onClick={() => { setForm(EMPTY_POINT); setEditingIndex(null); }} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset Input</button>
                    <button onClick={handleAdd} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">{editingIndex !== null ? "Update Point" : "Add SWOT Point"}</button>
                  </>
                }>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Quadrant</label>
                      <select value={form.quadrant} onChange={e => setForm(f => ({ ...f, quadrant: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select quadrant</option>
                        {["Strength","Weakness","Opportunity","Threat"].map(q => <option key={q}>{q}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Priority Level</label>
                      <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select priority</option>
                        {["High","Medium","Low"].map(p => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Point Title</label>
                      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Example: Strong discipline reputation among parents" className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Description / Context</label>
                      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Explain why this belongs in the selected quadrant..." rows={3} className="border border-[#e6ecf3] rounded-[10px] px-3 py-2 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] resize-y" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Source</label>
                      <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select source</option>
                        {["Diagnosis","Market Dynamics","Internal Observation","Leadership Input"].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Action Hint</label>
                      <input value={form.actionHint} onChange={e => setForm(f => ({ ...f, actionHint: e.target.value }))} placeholder="Example: strengthen through teacher training" className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                  </div>
                </Window>

                <Window title="Why SWOT matters" note="How this page sharpens strategy.">
                  {[
                    { title: "Turns findings into strategy language", text: "Diagnosis tells you what is happening. SWOT organizes that reality into a strategic decision frame." },
                    { title: "Improves goal-setting logic", text: "The next module should not create goals randomly. Goals should clearly respond to weaknesses, opportunities, and threats while building on strengths." },
                    { title: "Highlights strategic leverage", text: "A good SWOT does not just point out problems — it also shows where the school already has real advantages." },
                    { title: "Creates prioritization clarity", text: "Not every SWOT point deserves equal attention. Priority marking helps the school focus on what matters most first." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            {activeTab === "matrix" && (
              <>
                <div className="grid grid-cols-2 gap-[18px] mb-[18px]">
                  {QUADRANTS.map(({ q, bg, tagCls }) => {
                    const items = byQuadrant(q);
                    return (
                      <div key={q} className="bg-white border border-[#e6ecf3] rounded-[22px] overflow-hidden shadow-[0_8px_20px_rgba(25,35,52,.05)]">
                        <div className={`${bg} px-5 py-4 flex items-center justify-between border-b border-[#e6ecf3]`}>
                          <span className="text-[16px] font-semibold text-[#233445]">{q}s</span>
                          <Tag cls={tagCls}>{items.length} item{items.length !== 1 ? "s" : ""}</Tag>
                        </div>
                        <div className="p-4">
                          {!items.length ? (
                            <div className="text-center py-4 text-[13px] text-[#617587]">No {q.toLowerCase()}s added yet.</div>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {items.map((p, i) => (
                                <div key={i} className="border border-[#e6ecf3] rounded-[12px] p-3">
                                  <div className="flex justify-between gap-2 items-start mb-1">
                                    <span className="text-[13px] font-semibold text-[#233445]">{p.title}</span>
                                    <Tag cls={priorityCls(p.priority)}>{p.priority}</Tag>
                                  </div>
                                  <p className="text-[12px] text-[#617587]">{p.description}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Window title="All SWOT Points" note="Editable table view of everything added so far.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Search Point</label>
                      <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search point title…" className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Quadrant</label>
                      <select value={quadrantFilter} onChange={e => setQuadrantFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">All Quadrants</option>
                        {SWOT_QUADRANT_OPTIONS.map(q => <option key={q} value={q}>{q}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Priority</label>
                      <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">All Priorities</option>
                        {SWOT_PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Source</label>
                      <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">All Sources</option>
                        {SWOT_SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {["Quadrant","Point","Priority","Source","Actions"].map(h => (
                            <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {!filtered.length ? (
                          <tr><td colSpan={5} className="text-center py-6 text-[13px] text-[#617587]">{points.length ? "No SWOT points match the current filters." : "No SWOT points added yet."}</td></tr>
                        ) : filtered.map((p, i) => (
                          <tr key={i} className="hover:bg-[#fcfdff]">
                            <td className="px-[14px] py-[14px] border-b border-[#eef2f7]"><Tag cls={quadrantCls(p.quadrant)}>{p.quadrant}</Tag></td>
                            <td className="px-[14px] py-[14px] text-[13px] border-b border-[#eef2f7]">
                              <div className="font-semibold text-[#233445] mb-1">{p.title}</div>
                              <div className="text-[12px] text-[#617587]">{p.description}</div>
                            </td>
                            <td className="px-[14px] py-[14px] border-b border-[#eef2f7]"><Tag cls={priorityCls(p.priority)}>{p.priority}</Tag></td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{p.source || "-"}</td>
                            <td className="px-[14px] py-[14px] border-b border-[#eef2f7]">
                              <div className="flex gap-2">
                                <button onClick={() => handleEdit(points.indexOf(p))} className="px-3 py-1 rounded-[8px] bg-[#eef6fc] text-[#2d81c1] text-[11px] font-semibold hover:bg-[#cfe3f3]">Edit</button>
                                <button onClick={() => handleDelete(points.indexOf(p))} className="px-3 py-1 rounded-[8px] bg-[#fff0f0] text-[#d95353] text-[11px] font-semibold hover:bg-[#f4c5c5]">Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Window>
              </>
            )}

            {activeTab === "preview" && (
              <div className="grid grid-cols-2 gap-[18px]">
                <Window title="Live Strategic Summary" note="What the current SWOT is saying overall.">
                  {[
                    { label: "Strength Position", q: "Strength" },
                    { label: "Weakness Position", q: "Weakness" },
                    { label: "Opportunity Position", q: "Opportunity" },
                    { label: "Threat Position", q: "Threat" },
                  ].map((p, i) => {
                    const items = byQuadrant(p.q);
                    const value = items.length
                      ? `${items.length} ${p.q.toLowerCase()}(s) identified, including "${items[0].title}${items.length > 1 ? `" and ${items.length - 1} more` : '"'}.`
                      : `No ${p.q.toLowerCase()}s saved yet.`;
                    return (
                      <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                        <div className="text-[11px] font-semibold text-[#8ea0b1] uppercase tracking-wider mb-2">{p.label}</div>
                        <div className="text-[13px] text-[#233445]">{value}</div>
                      </div>
                    );
                  })}
                </Window>

                <Window title="Readiness Interpretation" note="How prepared the school is for the goals module.">
                  {[
                    { title: "Strategic clarity", text: interpretation },
                    { title: "Why this matters next", text: "The goals module should directly respond to the weaknesses, opportunities, and threats listed here while strengthening the school's strongest assets." },
                    { title: "Next connected page", text: "After this, move to Goals so the school can translate SWOT insights into strategic targets and outcomes." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mt-8 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1D3469]">Module Actions</h3>
                  <p className="text-sm text-gray-500">Save and manage module actions from one place.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={() => { if (!points.length) { showToast("Add at least one point first.", "error"); return; } persist(points, "SWOT draft saved"); showToast("SWOT draft saved.", "success"); }} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    Save Draft
                  </button>
                  <button onClick={() => { persist(points); showToast("SWOT saved.", "success"); }} className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Save Module
                  </button>
                  <button onClick={handleFillSample} className="border border-[#D67C2C] text-[#D67C2C] px-4 py-2 rounded-lg hover:bg-[#fff4eb] transition-colors font-medium">
                    Load Sample
                  </button>
                  <button onClick={handleClear} className="border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors font-medium">
                    Clear Module
                  </button>
                  <Link to="/sdp/dashboard" className="bg-[#1D3469] hover:bg-[#16284f] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* SQAAF SWOT Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-[0_32px_72px_rgba(19,31,47,.18)] w-full max-w-[680px] max-h-[80vh] flex flex-col mx-4">
            <div className="px-6 py-5 border-b border-[#e6ecf3] flex items-center justify-between gap-3">
              <div>
                <div className="text-[16px] font-semibold text-[#233445]">SQAAF-Generated SWOT Points</div>
                <div className="text-[12px] text-[#617587]">Select the points you want to import. Deselect any you don't need.</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPreviewSelected(previewPoints.map((_, i) => i))} className="px-3 py-[7px] rounded-[10px] border border-[#e6ecf3] text-[#617587] text-[12px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] transition-colors">Select All</button>
                <button onClick={() => setPreviewSelected([])} className="px-3 py-[7px] rounded-[10px] border border-[#e6ecf3] text-[#617587] text-[12px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] transition-colors">Deselect All</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-3">
              {previewPoints.map((p, i) => {
                const selected = previewSelected.includes(i);
                const qCls = p.quadrant === "Strength" ? "green" : p.quadrant === "Weakness" ? "red" : p.quadrant === "Opportunity" ? "blue" : "yellow";
                return (
                  <label key={i} className={`flex gap-3 p-4 rounded-[14px] border cursor-pointer transition-colors ${selected ? "border-[#2d81c1] bg-[#eef6fc]" : "border-[#e6ecf3] bg-[#fbfcfe] hover:bg-[#f5f9fd]"}`}>
                    <input type="checkbox" checked={selected} onChange={() => togglePreviewSelect(i)} className="mt-1 accent-[#2d81c1] w-4 h-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Tag cls={qCls}>{p.quadrant}</Tag>
                        <Tag cls={p.priority === "High" ? "red" : p.priority === "Medium" ? "orange" : "blue"}>{p.priority}</Tag>
                      </div>
                      <div className="text-[13px] font-semibold text-[#233445] mb-1">{p.title}</div>
                      <div className="text-[12px] text-[#617587]">{p.description}</div>
                      {p.actionHint && <div className="text-[11px] text-[#2d81c1] mt-1">→ {p.actionHint}</div>}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-[#e6ecf3] flex items-center justify-between gap-3">
              <div className="text-[12px] text-[#617587]">{previewSelected.length} of {previewPoints.length} point(s) selected</div>
              <div className="flex gap-2">
                <button onClick={() => { setShowPreviewModal(false); setPreviewPoints([]); setPreviewSelected([]); }} className="px-4 py-[10px] rounded-[12px] border border-[#e6ecf3] text-[#617587] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] transition-colors">Cancel</button>
                <button onClick={handlePreviewConfirm} disabled={previewSelected.length === 0} className="px-4 py-[10px] rounded-[12px] bg-[#2d81c1] text-white text-[13px] font-semibold hover:bg-[#266fa7] transition-colors disabled:opacity-50">Import {previewSelected.length} Point(s)</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
}
